import * as React from "react";
import { Transition } from "react-transition-group";
import { pure } from "recompose";
import styled from "styled-components";

import { Text } from "../../../../components/atoms";
import { DateTimeCard } from "../../../../components/molecules";
import { ScrollerContainer } from "../../../../containers/common";
import { ITherapistAvailability } from "../../../../services/api/therapists";
import { availableDateTime, dateFromIso } from "../../../../utils/index";

interface IProps {
  availableDates: ITherapistAvailability;
  isAvailableDatesLoading: boolean;
  isTherapistSelected?: boolean;
  onSelectTime(hour: number, minute: number): void;
  selectedDate: string;
  timeIndexSelected?: number;
}

const FilterTimeScrollerWrapper = styled.div`
  margin-bottom: 16px;

  ${(props: Partial<IProps>) =>
    !props.selectedDate ? `opacity: 0; visibility: hidden;` : `opacity: 1;`};
`;

const FilterTimeBlock = styled.div`
  display: inline-flex;

  > div {
    margin-right: 10px;
    &:last-child {
      margin-right: 0;
    }
  }
`;

const FilterTime: React.FC<IProps> = (props) => {
  const { date } = availableDateTime(props.selectedDate);
  const timeRange: string[] = [];

  if (props.availableDates && props.availableDates[date]) {
    props.availableDates[date].forEach((time) => {
      timeRange.push(dateFromIso(time).toFormat("HH:mm"));
    });
  }

  const duration = {
    enter: 300,
    exit: 300,
  };

  const filterTimeStyle = {
    transition: `all ${duration.enter}ms`,
  };

  const filterTimeStyles = {
    entered: { opacity: 1 },
    exited: { opacity: 0.2, transform: "scale(0.95)" },
  };

  const selectTime = (time: string): void => {
    const [hour, min] = time.split(":"); // split time to ["hours", "minutes"]
    props.onSelectTime(parseInt(hour, 10), parseInt(min, 10));
  };

  const mapTime = timeRange.map((time: string, index) => {
    const timeFormatted = dateFromIso(time).toFormat("h:mm a");
    const activatedTime =
      dateFromIso(props.selectedDate).toFormat("HH:mm") === time;

    return (
      <DateTimeCard
        active={activatedTime}
        key={index}
        onSelect={() => selectTime(time)}
        time={timeFormatted}
        type="time"
      />
    );
  });

  return (
    <React.Fragment>
      <Transition in={!props.isAvailableDatesLoading} timeout={duration}>
        {(state) => (
          <FilterTimeScrollerWrapper
            style={{
              ...filterTimeStyle,
              ...filterTimeStyles[state],
            }}
            selectedDate={props.selectedDate}
          >
            {!props.availableDates[date] &&
            !props.isAvailableDatesLoading &&
            !props.isTherapistSelected ? (
              <Text align="center" size="16" weight="semibold">
                No available time for selected date
              </Text>
            ) : !props.availableDates[date] &&
              !props.isAvailableDatesLoading &&
              props.isTherapistSelected ? null : ( // tslint:disable-next-line: no-null-keyword
              <ScrollerContainer
                itemIndexIn={props.timeIndexSelected}
                centerElementsIfMobile={true}
              >
                <FilterTimeBlock>{mapTime}</FilterTimeBlock>
              </ScrollerContainer>
            )}
          </FilterTimeScrollerWrapper>
        )}
      </Transition>
    </React.Fragment>
  );
};

const pureComponent = pure(FilterTime);

export { pureComponent as FilterTime };
